import { Box, Flex, Spinner, Text } from "@chakra-ui/react"
import { Table } from "@chakra-ui/react"
import { type ReactNode, memo } from "react"

export interface DataTableColumn<T = any> {
  /** Unique identifier for the column */
  key: string
  /** Display label for the column header */
  label: string
  /** Function to render the cell content */
  render?: (item: T) => ReactNode
  /** Whether the column is sortable */
  sortable?: boolean
  /** Column width */
  width?: string | number
  /** Text alignment */
  align?: "left" | "center" | "right"
}

export interface DataTableProps<T = any> {
  /** Array of data items to display */
  data: T[]
  /** Column definitions */
  columns: DataTableColumn<T>[]
  /** Loading state */
  loading?: boolean
  /** Empty state message */
  emptyMessage?: string
  /** Whether to show striped rows */
  striped?: boolean
  /** Table size variant */
  size?: "sm" | "md" | "lg"
  /** Custom empty state component */
  emptyState?: ReactNode
  /** Caption for the table */
  caption?: string
}

/**
 * DataTable Organism
 *
 * A flexible, reusable data table component that supports custom cell rendering,
 * loading states, and empty states. Designed to work with any data structure.
 */
export const DataTable = memo(
  <T,>({
    data,
    columns,
    loading = false,
    emptyMessage = "No data available",
    striped = true,
    size = "md",
    emptyState,
    caption,
  }: DataTableProps<T>) => {
    // Loading state
    if (loading) {
      return (
        <Box p={8} textAlign="center" data-testid="data-table-loading">
          <Spinner size="lg" />
          <Text mt={4} color="text.muted">
            Loading...
          </Text>
        </Box>
      )
    }

    // Empty state
    if (!data || data.length === 0) {
      if (emptyState) {
        return <Box data-testid="data-table-empty">{emptyState}</Box>
      }

      return (
        <Box p={8} textAlign="center" data-testid="data-table-empty">
          <Text color="text.muted">{emptyMessage}</Text>
        </Box>
      )
    }

    return (
      <Box overflow="auto" data-testid="data-table">
        <Table.Root variant={striped ? "outline" : "line"} size={size}>
          {caption && <Table.Caption>{caption}</Table.Caption>}

          <Table.Header>
            <Table.Row>
              {columns.map((column) => (
                <Table.ColumnHeader
                  key={column.key}
                  width={column.width}
                  textAlign={column.align}
                  data-testid={`header-${column.key}`}
                >
                  <Flex
                    alignItems="center"
                    justifyContent={
                      column.align === "center"
                        ? "center"
                        : column.align === "right"
                          ? "flex-end"
                          : "flex-start"
                    }
                  >
                    {column.label}
                    {column.sortable && (
                      <Box ml={2} opacity={0.6}>
                        ⇅
                      </Box>
                    )}
                  </Flex>
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {data.map((item, index) => (
              <Table.Row key={index} data-testid={`row-${index}`}>
                {columns.map((column) => (
                  <Table.Cell
                    key={column.key}
                    textAlign={column.align}
                    data-testid={`cell-${index}-${column.key}`}
                  >
                    {column.render
                      ? column.render(item)
                      : String((item as any)[column.key] || "")}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
    )
  },
) as <T>(props: DataTableProps<T>) => JSX.Element

// Set displayName on the inner component
const DataTableComponent = DataTable as any
DataTableComponent.displayName = "DataTable"
